import { RowAction } from '@components/data-grid/data-grid-types';
import { useDeleteModal } from '@components/generic-modals';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEdit } from '@components/icons/edit';
import { IconLoop } from '@components/icons/loop';
import { IconPause } from '@components/icons/pause';
import { IconPlay } from '@components/icons/play';
import { IconRemove } from '@components/icons/remove';
import { IconResume } from '@components/icons/resume';
import { useScopedTranslate } from '@locale/translate';

import { useIsOutOfCredits } from 'api/authentication/hooks';
import {
  useDeleteTravellerJourney,
  usePauseJourneyTraveller,
  useResetJourneyTraveller,
  useResumeJourneyTraveller,
  useStartJourneyTraveller,
} from 'api/traveller-journeys/hooks';
import { TravellerJourney, TravellerJourneyListItem, TravellerJourneyStatus } from 'api/types';
import { usePermissions } from 'contexts/permissions-context';

import { useCompanyTravellerToJourneySidebar } from './company-traveller-to-journey';
import { useMarkCompletedModal } from './mark-completed-modal';

import {
  BulkTravellerModalCount,
  StatisticsDataType,
} from '../../../../../components/bulk-traveller-modal-count';

export const getSingleTravellerStatisticsData = (status: TravellerJourneyStatus) => {
  return {
    total: 1,
    notStarted: status === 'not_started' ? 1 : 0,
    inProgress: status === 'in_progress' ? 1 : 0,
    paused: status === 'paused' ? 1 : 0,
    completed: status === 'completed' ? 1 : 0,
    toBeStarted: status === 'not_started' ? 1 : 0,
    toBePaused: status === 'in_progress' ? 1 : 0,
    toBeResumed: status === 'paused' ? 1 : 0,
    toBeReset: status !== 'not_started' ? 1 : 0,
    toBeRemoved: 1,
  };
};

export const useDeleteTravellerModal = ({
  data,
  statisticsData,
  onSuccess,
}: {
  data: { travellerJourneyId: string; journeyId: string };
  statisticsData: StatisticsDataType;
  onSuccess?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteTravellerJourney({
    id: data.travellerJourneyId,
    journey: data.journeyId,
    onSuccess,
  });

  return () =>
    showDeleteModal({
      title: t('deleteTravellers'),
      description: t('areYouSureDelete'),
      buttonText: t('core.remove'),
      hasUserConfirmation: true,
      onConfirm: () => deleteMutation.mutate(),
      children: <BulkTravellerModalCount type="remove" {...statisticsData} />,
    });
};

export const useResetTravellerModal = ({
  data,
  statisticsData,
}: {
  data: { travellerJourneyId: string; journeyId: string };
  statisticsData: StatisticsDataType;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const resetJourneyTravellerMutation = useResetJourneyTraveller(data);

  return () =>
    showDeleteModal({
      title: t('resetJourneys'),
      description: t('areYouSureReset'),
      hasUserConfirmation: true,
      buttonText: t('reset'),
      buttonIcon: <IconLoop />,
      onConfirm: () => resetJourneyTravellerMutation.mutate({}),
      children: <BulkTravellerModalCount type="reset" {...statisticsData} />,
    });
};

export const useTravellerActions = (traveller: TravellerJourney | TravellerJourneyListItem) => {
  const { t } = useScopedTranslate(locale);
  const isOutOfCredits = useIsOutOfCredits();
  const { canManageJourneyTravellers } = usePermissions();
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const data = { travellerJourneyId: traveller.shortId, journeyId: traveller.journey };
  const startJourneyTravellerMutation = useStartJourneyTraveller(data);
  const pauseJourneyTravellerMutation = usePauseJourneyTraveller(data);
  const resumeJourneyTravellerMutation = useResumeJourneyTraveller(data);
  const statisticsData = getSingleTravellerStatisticsData(traveller.status);
  const showDeleteModal = useDeleteTravellerModal({ data, statisticsData });
  const showResetModal = useResetTravellerModal({ data, statisticsData });
  const showMarkCompletedModal = useMarkCompletedModal();
  const finalStatus =
    isOutOfCredits && traveller.status === 'in_progress' ? 'paused' : traveller.status;

  const options: Array<RowAction<TravellerJourney | TravellerJourneyListItem>> = [
    ...(canManageJourneyTravellers
      ? [
          {
            label: t('editJourneyAttributes'),
            icon: <IconEdit />,
            onSelect: () => {
              showEditTravellerSidebar({
                journeyShortId: traveller.journey,
                journeyTravellerId: traveller.shortId,
              });
            },
          },
          'separator' as const,
        ]
      : []),
    ...(finalStatus === 'not_started'
      ? [
          {
            label: t('startJourney'),
            icon: <IconPlay />,
            onSelect: () => startJourneyTravellerMutation.mutate({}),
            isDisabled: () => isOutOfCredits,
          },
        ]
      : []),
    ...(finalStatus === 'in_progress'
      ? [
          {
            label: t('pauseJourney'),
            icon: <IconPause />,
            onSelect: () => pauseJourneyTravellerMutation.mutate({}),
            isDisabled: () => isOutOfCredits,
          },
        ]
      : []),

    ...(finalStatus === 'paused'
      ? [
          {
            label: t('resumeJourney'),
            icon: <IconResume />,
            onSelect: () => resumeJourneyTravellerMutation.mutate({}),
            isDisabled: () => isOutOfCredits,
          },
        ]
      : []),
    ...(canManageJourneyTravellers && finalStatus !== 'completed'
      ? [
          {
            label: t('markAsCompleted'),
            icon: <IconCheckmark />,
            onSelect: () =>
              showMarkCompletedModal({
                journeyId: traveller.journey,
                travellers: [traveller],
              }),
          },
        ]
      : []),
    {
      label: t('resetJourney'),
      icon: <IconLoop />,
      isDisabled: () => isOutOfCredits || traveller.status === 'not_started',
      onSelect: showResetModal,
    },
    ...(canManageJourneyTravellers
      ? [
          'separator' as const,
          {
            label: t('deleteTraveller'),
            icon: <IconRemove />,
            variant: 'danger' as const,
            onSelect: showDeleteModal,
            isDisabled: () => isOutOfCredits,
          },
        ]
      : []),
  ];

  return options;
};

export const locale = {
  en: {
    addAttribute: 'Add',
    completed: 'Completed',
    dateStart: 'First workday',
    emptyContent: 'There are no travellers currently in this journey',
    emptyHeader: 'No travellers in journey',
    clearFilters: 'Clear filters',
    inProgress: 'In progress',
    in_progress: 'In progress',
    nextUp: 'Next up',
    addFilters: 'Add filters',
    not_started: 'Not started',
    paused: 'Paused',
    loadingTravellers: 'Loading...',
    and: 'And',
    or: 'Or',
    progress: 'Progress',
    status: 'Status',
    supervisor: 'Supervisor',
    travellerList: 'Traveller list',
    remaining: 'Remaining',
    waitingToBeStarted: 'Waiting to be started',
    editJourneyAttributes: 'Edit journey fields',
    deleteTraveller: 'Remove from journey',
    startJourney: 'Start journey',
    startJourneyInfo: 'Start journey',
    showing: 'Showing {results} / {total} travellers',
    pauseJourney: 'Pause journey',
    resumeJourney: 'Resume journey',
    resetJourney: 'Reset journey',
    markAsCompleted: 'Mark as completed',
    resetJourneys: 'Reset journey/s',
    reset: 'Reset',
    areYouSureReset:
      'Are you sure you want to reset this travellers journey? The journey will be reset to its initial state and all progress will be lost',
    deleteTravellers: 'Remove traveller/s',
    areYouSureDelete:
      'Are you sure you want to remove the following traveller from this journey? All journey details and progress will be lost',
    notSet: 'Not set',
  },
};
